import { useMemo, useState } from 'react';
import { Building2, CalendarOff, Clock, Loader2, Palmtree, RotateCw, User, Users } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useTimeRocketHours } from '../hooks/useTimeRocketHours';
import type {
    AbsenceRow, HoursListResponse, HoursRow, HoursScope, HoursView, HoursViewer, VacationPlanThresholds,
} from '../types/timerocket';
import { PeriodPicker } from '../components/timerocket/PeriodPicker';
import { AbsencesKpiRow, KpiRow, VacationKpiRow } from '../components/timerocket/KpiRow';
import { HoursTable } from '../components/timerocket/HoursTable';
import { AbsencesTable } from '../components/timerocket/AbsencesTable';
import { VacationPlanTable } from '../components/timerocket/VacationPlanTable';
import { DaysModal } from '../components/timerocket/DaysModal';
import { DiscrepancyStrip } from '../components/timerocket/DiscrepancyBadges';
import { formatDateLong, formatRange, minutesAsDays } from '../components/timerocket/format';

/**
 * TimeRocket Hours — actual vs target hours, overtime balances and open vacation per person,
 * read from the nightly TimeRocket sync ([Reporting].[TimeRocketDailyTime] and friends). P3
 * added two views on the same scope: Absences (the period's buckets and rates) and the
 * Vacation plan (balance − planned to year end, no period).
 *
 * Three scopes, resolved SERVER-side from the login: "My hours" always answers; "My team" only
 * when the viewer has direct reports AND HR has switched the team view on; "All employees" for
 * page CanEdit. The tabs here merely mirror what the response says the viewer may see — the
 * server is the gate, and its 403 reason is shown verbatim because it is written for the user.
 *
 * The scope tabs come from the LAST successful response of any view, so a denied scope leaves
 * the tabs on screen and the user can step back to one that answers.
 */
export function TimeRocketHoursPage() {
    const { canView, isLoading: permLoading } = usePagePermissions('/timerocket-hours');
    const hours = useTimeRocketHours(canView);
    const [open, setOpen] = useState<{ employeeId: string; name: string; from: string; to: string } | null>(null);

    const data = hours.data;
    const absences = hours.absences;
    const vacation = hours.vacation;
    const view = hours.view;

    // The vacation account — the KPI tile and the table footer sum its days over the rows that
    // have a converted value. A person without a conversion rate contributes nothing rather than
    // a wrong zero, and "none at all" reads as n/a.
    const vacationAccount = useMemo(() => {
        if (!data) return { label: null as string | null, days: null as number | null };
        const account = data.accounts.find(a => (a.semantic ?? '').toUpperCase() === 'FERIEN') ?? null;
        if (!account) return { label: null, days: null };
        const days = data.rows.reduce<number | null>((s, r) => {
            const d = r.balances[account.accountId]?.days;
            if (d === null || d === undefined) return s;
            return (s ?? 0) + d;
        }, null);
        return { label: account.label, days };
    }, [data]);

    // The absence tiles need DAYS, and the summary carries minutes only: convert per person
    // (each with their own rate) and sum — a scope-wide "minutes / one rate" would be wrong for
    // any mix of full- and part-time contracts. Rows WITHOUT daily rows are skipped, exactly as
    // the server's summary and the table footer skip them: no target means no denominator, and
    // their minutes must not inflate the numerator either — so tile and footer agree with no
    // filter active.
    const absenceTotals = useMemo(() => {
        if (!absences) return { targetDays: 0, vacationDays: null as number | null, holidayDays: null as number | null };
        const counted = absences.rows.filter(r => !r.noDailyRows);
        const sumDays = (pick: (r: AbsenceRow) => number) => counted.reduce<number | null>((s, r) => {
            const d = minutesAsDays(pick(r), r.conversionRate);
            return d === null ? s : (s ?? 0) + d;
        }, null);
        return {
            targetDays: counted.reduce((s, r) => s + r.targetDays, 0),
            vacationDays: sumDays(r => r.coarseMin.FERIEN),
            holidayDays: sumDays(r => r.coarseMin.FEIERTAG),
        };
    }, [absences]);

    if (permLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
        );
    }

    if (!canView) {
        return (
            <div className="flex h-64 items-center justify-center">
                <p className="text-gray-500">You do not have access to this page.</p>
            </div>
        );
    }

    const viewer = hours.viewer;
    const showTeamTab = viewer?.isManager === true;
    const teamEnabled = viewer?.isManager === true && viewer.teamViewEnabled;
    const showAllTab = viewer?.canSeeAll === true;

    const current = view === 'hours' ? data : view === 'absences' ? absences : vacation;
    const hasPeriod = view !== 'vacation';

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-baseline justify-between gap-4">
                <div className="flex items-baseline gap-4">
                    <h1 className="text-2xl font-bold text-gray-900">TimeRocket Hours</h1>
                    <p className="text-xs italic text-gray-500">{SUBTITLE[view]}</p>
                </div>
                {view === 'hours' && data?.asOf && (
                    <span className="text-xs text-gray-500 whitespace-nowrap" title="Latest balance date among the people shown">
                        Balances as of {formatDateLong(data.asOf)}
                    </span>
                )}
                {view === 'vacation' && vacation && (
                    <span
                        className="text-xs text-gray-500 whitespace-nowrap"
                        title="The vacation plan has no period: planned entries from tomorrow to 31 December. 'Plan as of' is the day the planned entries were last fetched from TimeRocket — a plan older than a balance is marked ≈ in the table."
                    >
                        Today to year end · {formatRange(vacation.today, vacation.yearEnd)}
                        {vacation.plannedAsOf && <> · plan as of {formatDateLong(vacation.plannedAsOf)}</>}
                    </span>
                )}
            </div>

            <div className="border-b border-gray-200">
                <nav className="flex gap-6">
                    <TabButton
                        active={hours.scope === 'own'} onClick={() => hours.setScope('own')}
                        icon={User} label={SCOPE_OWN_LABEL[view]} hint="Your own figures"
                    />
                    {showTeamTab && (
                        <TabButton
                            active={hours.scope === 'team'} onClick={() => hours.setScope('team')}
                            icon={Users} label="My team"
                            hint={teamEnabled ? 'Your direct reports' : 'Team view not enabled yet'}
                            disabled={!teamEnabled}
                        />
                    )}
                    {showAllTab && (
                        <TabButton
                            active={hours.scope === 'all'} onClick={() => hours.setScope('all')}
                            icon={Building2} label="All employees" hint="Every active person in TimeRocket"
                        />
                    )}
                </nav>
            </div>

            {/* The view row: same tab style, one step smaller in weight, so the eye reads
                "who" (scope) first and "what" (view) second. */}
            <div className="border-b border-gray-200">
                <nav className="flex gap-6">
                    <TabButton active={view === 'hours'} onClick={() => hours.setView('hours')} icon={Clock} label="Hours" hint="Actual vs target, balances" />
                    <TabButton active={view === 'absences'} onClick={() => hours.setView('absences')} icon={CalendarOff} label="Absences" hint="Holidays, vacation and absences of the period, with the absence rate" />
                    <TabButton active={view === 'vacation'} onClick={() => hours.setView('vacation')} icon={Palmtree} label="Vacation plan" hint="Balance today, planned to year end, projected rest" />
                </nav>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                {hasPeriod ? (
                    <PeriodPicker
                        period={hours.period}
                        canShiftForward={hours.canShiftForward}
                        onPreset={hours.setPreset}
                        onShift={hours.shiftPeriod}
                        onCustom={hours.setCustom}
                    />
                ) : (
                    <span className="text-sm text-gray-500">
                        No period — the plan always runs from today to the end of the year.
                    </span>
                )}
                <button
                    onClick={hours.reload}
                    disabled={hours.loading}
                    className="inline-flex h-8 items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                    <RotateCw className={`h-4 w-4 ${hours.loading ? 'animate-spin' : ''}`} />
                    Reload
                </button>
            </div>

            {(hours.error ?? hours.notice) && (
                <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{hours.error ?? hours.notice}</div>
            )}

            {/* The last good answer of the CURRENT view stays on screen under an error banner (a 400
                for a too-long custom range must not blank the page); the banner says what to fix. */}
            {view === 'hours' && data && (
                <>
                    <KpiRow summary={data.summary} openVacationDays={vacationAccount.days} vacationLabel={vacationAccount.label} />
                    <DiscrepancyStrip summary={data.discrepancies} />

                    <HoursTable
                        rows={data.rows}
                        accounts={data.accounts}
                        summary={data.summary}
                        viewer={data.viewer}
                        loading={hours.loading}
                        emptyMessage={emptyMessageFor(hours.scope, data.scope, data.viewer, 'No hours recorded for this period.')}
                        onOpenDays={(row: HoursRow) => { if (row.employeeId !== null) setOpen({ employeeId: row.employeeId, name: row.name, from: data.from, to: data.to }); }}
                    />

                    {data.admin && <AdminStrip admin={data.admin} />}
                </>
            )}

            {view === 'absences' && absences && (
                <>
                    <AbsencesKpiRow
                        summary={absences.summary}
                        typesVisible={absences.typesVisible}
                        targetDays={absenceTotals.targetDays}
                        vacationDays={absenceTotals.vacationDays}
                        holidayDays={absenceTotals.holidayDays}
                    />
                    <DiscrepancyStrip summary={absences.discrepancies} />

                    <AbsencesTable
                        rows={absences.rows}
                        summary={absences.summary}
                        typesVisible={absences.typesVisible}
                        viewer={absences.viewer}
                        loading={hours.loading}
                        emptyMessage={emptyMessageFor(hours.scope, absences.scope, absences.viewer, 'No absences recorded for this period.')}
                        onOpenDays={(row: AbsenceRow) => { if (row.employeeId !== null) setOpen({ employeeId: row.employeeId, name: row.name, from: absences.from, to: absences.to }); }}
                    />

                    {!absences.typesVisible && (
                        <p className="text-xs text-gray-500">
                            Absence types are health data — the team view shows the tenant&apos;s groups only (Feiertag · Ferien · Absenz · Auswärts).
                        </p>
                    )}
                </>
            )}

            {view === 'vacation' && vacation && (
                <>
                    <VacationKpiRow summary={vacation.summary} />
                    <DiscrepancyStrip summary={vacation.discrepancies} />

                    <VacationPlanTable
                        rows={vacation.rows}
                        summary={vacation.summary}
                        loading={hours.loading}
                        emptyMessage={emptyMessageFor(hours.scope, vacation.scope, vacation.viewer, 'No vacation balance synced yet.')}
                    />

                    <ThresholdLegend thresholds={vacation.thresholds} />
                </>
            )}

            {!current && hours.loading && (
                <div className="flex h-40 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
            )}

            {open && (
                <DaysModal
                    isOpen
                    onClose={() => setOpen(null)}
                    employeeId={open.employeeId}
                    name={open.name}
                    from={open.from}
                    to={open.to}
                    // The account labels for the modal's history table come with the hours
                    // response; opened from the absences view before Hours was ever loaded, the
                    // history shows dates only — the days table, which is what the click is for,
                    // does not need them.
                    accounts={data?.accounts ?? []}
                />
            )}
        </div>
    );
}

const SUBTITLE: Record<HoursView, string> = {
    hours: 'Actual vs target hours, overtime balances and open vacation — synced nightly from TimeRocket.',
    absences: 'Holidays, vacation and absences of the period as days, with the absence rate — synced nightly from TimeRocket.',
    vacation: 'Open vacation today, planned to year end and the projected rest — synced nightly from TimeRocket.',
};

const SCOPE_OWN_LABEL: Record<HoursView, string> = {
    hours: 'My hours',
    absences: 'My absences',
    vacation: 'My vacation',
};

/**
 * Why the list is empty — for the own scope the viewer's resolution says it; elsewhere it is
 * just empty. `requested` is the tab the user is on, `answered` the scope the response is for;
 * they differ for one render after a fall-back, and the message must describe the answer.
 */
function emptyMessageFor(requested: HoursScope, answered: HoursScope, viewer: HoursViewer, fallback: string): string {
    if (requested === 'own') {
        if (viewer.resolution === 'no-employee-master') return 'Your login e-mail is not found in the employee master. Ask HR to add it.';
        if (viewer.resolution === 'no-timerocket') return 'You are not in TimeRocket (or not yet matched). Ask HR.';
    }
    // Every list carries a row per MATCHED person even with zero figures, so an empty team list
    // means "nobody of yours is matched in TimeRocket", not "nothing in this period".
    if (answered === 'team') return 'None of your direct reports is matched in TimeRocket yet. Ask HR.';
    return fallback;
}

/** The colour rule of the vacation plan, spelled out from the server's thresholds — not hard-coded. */
function ThresholdLegend({ thresholds }: { thresholds: VacationPlanThresholds }) {
    const month = MONTHS_LONG[thresholds.fromMonth - 1] ?? `month ${thresholds.fromMonth}`;
    return (
        <p className="text-xs text-gray-500">
            {thresholds.active
                ? `Yellow ≥ ${thresholds.yellowDays} d, red ≥ ${thresholds.redDays} d rest from ${month} on; a negative rest is always red.`
                : `Thresholds apply from ${month} (yellow ≥ ${thresholds.yellowDays} d, red ≥ ${thresholds.redDays} d rest); a negative rest is always red.`}
        </p>
    );
}

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function AdminStrip({ admin }: { admin: NonNullable<HoursListResponse['admin']> }) {
    const run = admin.lastRun;
    const troubled = run !== null && (run.status === 'PARTIAL' || run.status === 'FAILED');
    return (
        <div className={`text-xs ${troubled ? 'text-amber-700' : 'text-gray-500'}`}
             title={troubled ? (run?.warnings ?? 'no warnings recorded') : undefined}>
            {admin.unmatched} TimeRocket person(s) not matched
            {run && (
                <>
                    {' · '}last sync #{run.runId} {run.status} · {run.apiCalls} calls
                </>
            )}
            {!run && ' · no sync run recorded yet'}
        </div>
    );
}

function TabButton({ active, onClick, icon: Icon, label, hint, disabled = false }: {
    active: boolean; onClick: () => void;
    icon: React.ComponentType<{ className?: string }>; label: string; hint: string; disabled?: boolean;
}) {
    return (
        <button
            onClick={onClick}
            title={hint}
            disabled={disabled}
            className={`flex items-center gap-2 pb-3 border-b-2 text-sm font-medium transition-colors ${
                active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:border-transparent`}
        >
            <Icon className="w-4 h-4" />
            {label}
        </button>
    );
}

export default TimeRocketHoursPage;
